/**
 * Axios Configuration - Global Setup
 *
 * This file configures axios interceptors to automatically:
 * 1. Add authentication tokens to all API requests
 * 2. Handle authentication errors globally with a friendly SweetAlert prompt
 * 3. Redirect to login (with redirect-back) when the user confirms
 *
 * The Visual Editor's save flow handles 401 locally (it shows a tailored
 * "Session expired — work is safely saved" dialog so the designer doesn't
 * lose unsaved JSON). For those URLs we skip the global handler and let
 * the page-level catch take over.
 */

import axios from 'axios';
import Swal from 'sweetalert2';

// URL patterns the global 401 handler must NOT intercept. The page that
// owns the request handles its own 401 (typically because it has unsaved
// state to protect).
const SKIP_GLOBAL_401_PATTERNS = [
  // Visual editor save endpoints — useEditorSave.js shows its own dialog
  /\/api\/template\/[^/]+\/save\b/,
  /\/api\/template\/[^/]+\/page\/[^/]+\/save\b/,
  /\/api\/template\/[^/]+\/draft\b/,
  // Token endpoints — login/refresh failures aren't "session expired"
  /\/api\/token\/?(refresh\/?)?$/,
];

const isSkippedUrl = (url) => {
  if (!url) return false;
  return SKIP_GLOBAL_401_PATTERNS.some((re) => re.test(url));
};

const PUBLIC_PATHS = ['/login', '/register', '/Register', '/forgot-password'];
const isOnPublicPath = () => {
  const path = window.location.pathname || '';
  return PUBLIC_PATHS.some((p) => path.startsWith(p));
};

// We can be hit by a flurry of 401s (sidebar polling + page polling +
// in-flight user request) all at once. Show ONE dialog, not five.
let sessionExpiredDialogOpen = false;

const clearTokens = () => {
  try {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('authToken');
  } catch (_) { /* ignore */ }
};

// Single-flight token refresh. A flurry of 401s must trigger AT MOST one
// refresh call; everyone else awaits the same promise. Returns true if the
// access token was successfully refreshed, false otherwise.
let refreshPromise = null;

const tryRefreshToken = () => {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    let refreshToken = null;
    try {
      // AuthContext stores tokens as JSON under 'authToken' ({access, refresh})
      // and mirrors access -> 'access_token'. It never writes a standalone
      // 'refresh_token' key, so read the refresh from authToken (legacy key as
      // fallback) — otherwise this refresh path could never fire and every 401
      // jumped straight to the "session expired" dialog.
      const raw = localStorage.getItem('authToken');
      if (raw) {
        try { refreshToken = JSON.parse(raw)?.refresh || null; } catch (_) { /* ignore */ }
      }
      if (!refreshToken) refreshToken = localStorage.getItem('refresh_token');
    } catch (_) { /* ignore */ }
    if (!refreshToken) return false;

    try {
      // Use a bare axios (no interceptors) so a 401 from the refresh
      // endpoint doesn't recurse back into this handler.
      const resp = await axios.post(
        '/api/token/refresh/',
        { refresh: refreshToken },
        { headers: { 'Content-Type': 'application/json' }, _skipAuthRefresh: true }
      );
      const newAccess = resp?.data?.access;
      if (!newAccess) return false;
      try {
        localStorage.setItem('access_token', newAccess);
        // Keep the canonical authToken JSON's access in sync so AuthContext-
        // driven calls also see the refreshed token.
        const rawTok = localStorage.getItem('authToken');
        if (rawTok) {
          const obj = JSON.parse(rawTok);
          obj.access = newAccess;
          localStorage.setItem('authToken', JSON.stringify(obj));
        }
      } catch (_) { /* ignore */ }
      return true;
    } catch (_) {
      return false;
    }
  })();

  // Reset the gate once settled so a later genuine expiry can refresh again.
  refreshPromise.finally(() => { refreshPromise = null; });
  return refreshPromise;
};

const showSessionExpiredDialog = () => {
  if (sessionExpiredDialogOpen) return;
  if (isOnPublicPath()) return; // already on /login etc — nothing to do
  sessionExpiredDialogOpen = true;

  Swal.fire({
    icon: 'warning',
    title: 'Session expired',
    html: '<p style="color:#64748b;font-size:14px;margin-top:4px">Your sign-in expired. Sign in again to continue.</p>',
    confirmButtonText: 'Sign in again',
    confirmButtonColor: '#d45614',
    showCancelButton: true,
    cancelButtonText: 'Stay here',
    allowOutsideClick: false,
    allowEscapeKey: false,
  }).then((result) => {
    sessionExpiredDialogOpen = false;
    if (result.isConfirmed) {
      // Only now do we wipe tokens and bounce to login. Leaving tokens
      // intact until the user confirms avoids cascading logout across tabs
      // on a spurious 401.
      clearTokens();
      const path = window.location.pathname + window.location.search;
      const redirect = encodeURIComponent(path);
      window.location.href = `/login?redirect=${redirect}`;
    }
    // On cancel ("Stay here"): do NOT clear tokens. Whatever stale data was
    // already rendered stays visible and the existing session is preserved.
  });
};

// Request Interceptor - Automatically adds auth token
axios.interceptors.request.use(
  (config) => {
    // Get token from localStorage
    const token = localStorage.getItem('access_token');

    // Add Authorization header if token exists and not already set
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response Interceptor - Handles auth errors
axios.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    // Handle 401 Unauthorized - Token expired or invalid
    if (error.response?.status === 401) {
      // Determine which URL this came from. Different sources expose it
      // differently (request config, original config). Cover both.
      const originalConfig = error.config || {};
      const url =
        originalConfig.url ||
        error.response?.config?.url ||
        '';

      // Pages that own their own 401 handler (visual editor) get to manage
      // their own UX. Just bubble the error up to them. Also skip the
      // refresh endpoint itself (its own 401 means the refresh token died).
      if (isSkippedUrl(url) || originalConfig._skipAuthRefresh) {
        return Promise.reject(error);
      }

      // Attempt a token refresh BEFORE wiping anything. Only retry the
      // original request once (guarded by _retried) to avoid loops.
      if (!originalConfig._retried) {
        originalConfig._retried = true;
        const refreshed = await tryRefreshToken();
        if (refreshed) {
          try {
            const newAccess = localStorage.getItem('access_token');
            if (newAccess) {
              originalConfig.headers = originalConfig.headers || {};
              originalConfig.headers.Authorization = `Bearer ${newAccess}`;
            }
          } catch (_) { /* ignore */ }
          // Replay the original request with the fresh token.
          return axios(originalConfig);
        }
      }

      // Refresh failed (or already retried): surface the friendly dialog.
      // Tokens are only cleared if the user confirms re-login.
      showSessionExpiredDialog();
    }

    // 500 / network errors: bubble up so the caller can decide how to display.
    // Never use a blocking alert().
    return Promise.reject(error);
  }
);

/**
 * Shared authenticated axios instance.
 *
 * HIGH-19: Pages used to call `axios.create()` to get their own instance,
 * but a fresh instance does NOT inherit the global request/response
 * interceptors configured above — so their 401s silently bypassed the
 * session-expired handler (no re-auth prompt, no refresh).
 *
 * This is the default global `axios` (which HAS the interceptors). It is
 * exported under the name `authAxios` so consumers can import it directly
 * instead of creating a detached instance. The interface is identical to
 * what `axios.create()` returned (get/post/put/delete, etc.), so callers
 * only need to swap the import.
 */
export const authAxios = axios;

export default axios;
